function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useState, useEffect, Fragment } from "react";
import styled from "styled-components";
import { TextField } from "../";
import { Footnote } from "../../";
import { ReactComponent as CaretIcon } from "@svgr/webpack?-svgo,+titleProp,+ref!./Caret_Icon.svg";
const SelectField = styled(TextField).attrs(() => ({
  as: "div"
}))`
  padding-top: 8px;
  padding-bottom: 8px;
  padding-left: ${props => props.icon ? "35px" : "12px"};
  padding-right: 30px;
  position: relative;
  z-index: 1;
  ${props => props.open && `border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    border-bottom-color: ${props.theme.colors.selectBottomBorder};
    box-shadow: 0 -1px 8px 0 rgba(0, 0, 0, 0.24);
    z-index: 4;`};
  &:focus {
    border-color: ${props => props.theme.colors.border};
    border-bottom-color: ${props => props.theme.colors.selectBottomBorder};
    box-shadow: 0 -1px 8px 0 rgba(0, 0, 0, 0.24);
  }
  ${props => props.disabled && `background-color: ${props.theme.colors.buttonPrimaryBgDisabled};
    border-color: ${props.theme.colors.buttonPrimaryTextDisabled};
    pointer-events: none;`};
  ${props => props.error && `background-color: ${props.theme.colors.inputErrorBg};`};
`;
const Dropdown = styled.div`
  position: absolute;
  display: block;
  top: 100%;
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid ${props => props.theme.colors.border};
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  border-top: 0;
  box-shadow: 0 1px 8px 0 rgba(0, 0, 0, 0.24);
  max-height: 224px;
  opacity: 0;
  overflow-x: hidden;
  overflow-y: auto;
  visibility: hidden;
  pointer-events: none;
  z-index: 10;
  ${props => props.open && `opacity: 1;
    visibility: visible;
    pointer-events: all;`}
`;
const Item = styled.div`
  align-items: flex-start;
  background-color: ${props => props.selected ? props.theme.colors.selectItemHover : "#FFFFFF"};
  border-bottom: 1px solid ${props => props.theme.colors.selectItemDivider};
  box-sizing: border-box;
  color: ${props => props.theme.colors.inputText};
  cursor: pointer;
  display: flex;
  ${props => props.theme.fonts.default}
  flex-flow: column wrap;
  font-weight: ${props => props.selected ? 600 : 400};
  justify-content: ${props => props.twoLine ? "flex-start" : "center"};
  line-height: 22px;
  ${props => props.paddingChange && `min-height: 71px;`};
  position: relative;
  padding: ${props => props.paddingChange ? "15px 12px 15px 67px" : "17px 12px"};
  &:hover {
    cursor: pointer;
    background-color: ${props => props.theme.colors.selectItemHover};
  }
  &:last-of-type {
    border-bottom: 0;
  }
`;
const SubTitle = styled(Footnote)`
  display: block;
  margin-top: 4px;
  width: 100%;
`;
const Selected = styled.span`
  align-items: center;
  color: ${props => props.placeholderValue ? props.theme.colors.inputPlaceholderText : props.theme.colors.inputText};
  display: ${props => props.withImage ? "flex" : "block"};
  ${props => props.theme.fonts.default};
  font-size: ${props => props.placeholderValue ? "12px" : "17px"};
  font-weight: ${props => props.placeholderValue ? 300 : 600};
  justify-content: ${props => props.withImage ? "flex-start" : "space-between"};
  line-height: 22px;
  overflow: hidden;
  position: relative;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${props => props.disabled && `color: ${props.theme.colors.inputPlaceholderText};`}
`;
const Icon = styled.div`
  position: absolute;
  left: 12px;
`;
export const Select = ({
  options,
  disabled,
  icon,
  placeholder,
  selected = {},
  country = false,
  onChange,
  ...props
}) => {
  const [openState, setOpenState] = useState(false);
  const [fieldRef, setFieldRef] = useState(false);

  const handleClickOutside = evt => {
    if (!fieldRef.contains(evt.target)) {
      setOpenState(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  });
  return React.createElement(FieldWrapper, {
    country: country,
    ref: component => setFieldRef(component)
  }, React.createElement(SelectField, _extends({
    open: openState,
    icon: !!icon,
    disabled: disabled
  }, props, {
    onClick: () => setOpenState(!openState)
  }), !!icon && React.createElement(Icon, null, icon), React.createElement(Selected, {
    disabled: disabled,
    withImage: !!selected.image,
    placeholderValue: selected.id ? false : true
  }, !!selected.image ? React.createElement(Fragment, null, React.createElement(SelectedImageMask, null, React.createElement(SelectedImage, {
    src: selected.image
  })), selected.value.toUpperCase()) : React.createElement(Fragment, null, selected.id ? selected.text : placeholder)), React.createElement(Caret, {
    disabled: disabled
  })), React.createElement(Dropdown, {
    open: openState
  }, options.map(option => {
    const {
      text,
      image,
      id,
      subtext
    } = option;
    return React.createElement(Item, {
      key: id,
      selected: id === selected.id,
      onClick: () => {
        onChange(option);
        setOpenState(false);
      },
      paddingChange: !!image,
      twoLine: !!subtext
    }, !!subtext ? React.createElement(Fragment, null, !!image && React.createElement(LabelImageContainer, null, React.createElement(LabelImage, {
      src: image
    })), text, React.createElement(SubTitle, null, subtext)) : React.createElement(Fragment, null, !!image && React.createElement(LabelImageContainer, null, React.createElement(LabelImage, {
      src: image
    })), text));
  })));
};
const SelectedImageMask = styled.div`
  align-items: center;
  overflow: hidden;
  border-radius: 50%;
  display: inline-flex;
  justify-content: center;
  position: relative;
  width: 16px;
  height: 16px;
  margin-right: 12px;
`;
const SelectedImage = styled.img`
  display: block;
  height: auto;
  width: 32px;
  position: relative;
`;
const LabelImageContainer = styled.div`
  align-items: center;
  display: inline-flex;
  justify-content: center;
  /* margin-right: 12px; */
  border-radius: 50%;
  overflow: hidden;
  height: 40px;
  width: 40px;
  position: absolute;
  left: 12px;
`;
const LabelImage = styled.img`
  display: block;
  height: auto;
  width: 76px;
  position: relative;
`;
export const FieldWrapper = styled.div`
  background: #ffffff;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  ${props => !props.country && "position: relative"};
  label > & {
    margin-top: 9px;
  }
`;
export const Caret = styled(CaretIcon)`
  height: 6px;
  position: absolute;
  right: 15px;
  top: 17px;
  width: 10px;
  z-index: 1;
  path {
    fill: ${props => props.disabled ? props.theme.colors.disabledText : props.theme.colors.inputText};
  }
`;